import api from "./api";

class AirwallexService {
    /**
     * Get Airwallex authentication token.
     * Token is cached on backend (25 minutes).
     */
    getTokens() {
        return api.post("/airwallex/get-tokens");
    }

    /**
     * Create a Payment Intent.
     * @param {Object} dataForm { amount, merchant_order_id, descriptor, metadata, ... }
     */
    createPaymentIntent(dataForm) {
        return api.post("/airwallex/create-payment-intent", dataForm);
    }

    /**
     * Send Payment Intent to terminal for card swipe.
     * @param {Object} dataForm { terminal_id, payment_intent_id, payment_method_options? }
     */
    processPaymentIntent(dataForm) {
        return api.post("/airwallex/process-payment-intent", dataForm);
    }

    /**
     * Confirm a Payment Intent with PayNow to get the QR code payload.
     * Backend response: data.next_action.qrcode (raw PayNow payload string)
     * Render the payload as a QR image on the client.
     * @param {Object} dataForm { payment_intent_id, shopper_name?, return_url? }
     */
    confirmPaymentIntentPayNow(dataForm) {
        return api.post("/airwallex/confirm-payment-intent-paynow", dataForm);
    }

    /**
     * Cancel Payment Intent (auth reversal).
     * Backend has built-in race condition protection.
     * @param {Object} dataForm { payment_intent_id, cancellation_reason? }
     */
    cancelPaymentIntent(dataForm) {
        return api.post("/airwallex/cancel-payment-intent", dataForm);
    }

    /**
     * Poll payment intent status (SUCCEEDED / FAILED / REQUIRES_CAPTURE / ...).
     * @param {Object} dataForm { payment_intent_id }
     */
    getPaymentIntentStatus(dataForm) {
        return api.post("/airwallex/get-payment-intent-status", dataForm);
    }

    /**
     * Retrieve terminal info (status, serial number, passwords).
     * @param {Object} dataForm { terminal_id }
     */
    retrieveTerminal(dataForm) {
        return api.post("/airwallex/retrieve-terminal", dataForm);
    }

    /**
     * Open the Hosted Payment Page for an intent returned by /airwallex/checkout-hpp.
     * Airwallex requires HPP to be launched through their JS SDK
     * (redirectToCheckout) rather than a hand-built URL — the page's own
     * route/params are internal to them and can change.
     * The SDK is imported on demand so it only loads when a shopper pays with
     * Airwallex.
     * `env` belongs to init(), not redirectToCheckout(). No failUrl: Airwallex
     * deprecates it — a failed payment is retried on the HPP itself, and the
     * outcome is confirmed server-side (check-hpp / payment_intent.succeeded).
     * @param {Object} data { payment_intent_id, client_secret, currency, country_code, env, success_url }
     * @param {Function} [onBack] called when Back from the HPP restores this page
     *   from the browser's back/forward cache — the caller's loader is still up
     *   there, since the page was frozen mid-redirect.
     */
    async redirectToHpp(data, onBack) {
        const handlePageShow = (event) => {
            if (event.persisted) {
                window.removeEventListener("pageshow", handlePageShow);
                onBack();
            }
        };
        if (onBack) {
            window.addEventListener("pageshow", handlePageShow);
        }

        try {
            const { init } = await import("@airwallex/components-sdk");
            const { payments } = await init({
                env: data.env,
                enabledElements: ["payments"],
            });

            payments.redirectToCheckout({
                intent_id: data.payment_intent_id,
                client_secret: data.client_secret,
                currency: data.currency,
                country_code: data.country_code,
                successUrl: data.success_url,
                // Apple Pay needs the merchant's country (`country_code` above
                // is the shopper's); without it the HPP leaves Apple Pay out
                // while Google Pay still shows.
                applePayRequestOptions: { countryCode: "SG" },
            });
        } catch (error) {
            window.removeEventListener("pageshow", handlePageShow);
            throw error;
        }
    }
}

export default new AirwallexService();
